import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ZenCyclery.css';

const HERO   = 'https://images.unsplash.com/photo-1485965120184-e220f721d03e?w=1600&q=80';
const ROAD   = 'https://images.unsplash.com/photo-1571333250630-f0230c320b6d?w=900&q=80';
const MTB    = 'https://images.unsplash.com/photo-1544191696-102dbeb8cf63?w=900&q=80';

const CONFIG = {
    road: {
        hubs: [
            { id: 'ck-r45c', label: 'Chris King R45', sub: 'Ceramic bearings' },
            { id: 'ck-r45',  label: 'Chris King R45', sub: 'Steel bearings'   },
            { id: 'wi-t11',  label: 'White Industries T11', sub: 'Alloy body' },
            { id: 'dt-240',  label: 'DT Swiss 240', sub: 'Swiss precision'    },
        ],
        rims: [
            { id: 'enve-34', label: 'ENVE 3.4', sub: '45mm carbon clincher' },
            { id: 'enve-22', label: 'ENVE 2.2', sub: '38mm carbon clincher' },
            { id: 'enve-45', label: 'ENVE SES 4.5', sub: '45mm aero carbon' },
            { id: 'hunt-34', label: 'Hunt 34 Carbon', sub: '34mm carbon'    },
        ],
        spokes: [
            { id: 'cx-ray',  label: 'Sapim CX-Ray', sub: 'Bladed, lightest'     },
            { id: 'aerolite',label: 'DT Swiss Aerolite', sub: 'Bladed'          },
            { id: 'comp',    label: 'DT Swiss Competition', sub: 'Round 2.0/1.8' },
        ],
    },
    mtb: {
        hubs: [
            { id: 'ck-boost', label: 'Chris King Boost', sub: 'Boost spacing, ceramic' },
            { id: 'ck-cl',    label: 'Chris King Classic', sub: 'Classic spacing'       },
            { id: 'wi-mr30',  label: 'White Industries MR30', sub: 'Alloy freehub'      },
            { id: 'dt-350',   label: 'DT Swiss 350', sub: 'Classic Swiss reliability'  },
        ],
        rims: [
            { id: 'enve-m525', label: 'ENVE M525', sub: '27.5" carbon'      },
            { id: 'enve-m630', label: 'ENVE M630', sub: '29" carbon'        },
            { id: 'enve-m735', label: 'ENVE M735', sub: '29" enduro carbon' },
            { id: 'hunt-mtb',  label: 'Hunt Trail Wide', sub: '30mm internal width' },
        ],
        spokes: [
            { id: 'cx-ray-m', label: 'Sapim CX-Ray', sub: 'Bladed, lightest'        },
            { id: 'race',     label: 'Sapim Race', sub: 'Double-butted 2.0/1.8'     },
            { id: 'strong',   label: 'Sapim Strong', sub: 'Double-butted 2.3/2.0'   },
        ],
    },
};

const PICKS = [
    {
        name: 'The Climber',
        type: 'Road',
        hub: 'Chris King R45',
        rim: 'ENVE 2.2',
        spoke: 'Sapim CX-Ray',
        weight: '1,285g',
        desc: 'Featherlight and stiff. Built for long days in the mountains where every gram earns its keep.',
        img: ROAD,
    },
    {
        name: 'The All-Rounder',
        type: 'Road',
        hub: 'White Industries T11',
        rim: 'ENVE 3.4',
        spoke: 'DT Swiss Aerolite',
        weight: '1,420g',
        desc: "Fast on the flats, capable in the hills. The wheel we'd put under most riders.",
        img: ROAD,
    },
    {
        name: 'The Trail Rig',
        type: 'MTB',
        hub: 'Chris King Boost',
        rim: 'ENVE M630',
        spoke: 'Sapim Race',
        weight: '1,680g',
        desc: 'Stiff, tubeless-ready, and built to absorb years of trail abuse without a second thought.',
        img: MTB,
    },
];

const ZenCyclery = () => {
    const [bikeType, setBikeType] = useState('road');
    const [hub, setHub] = useState(null);
    const [rim, setRim] = useState(null);
    const [spoke, setSpoke] = useState(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        document.documentElement.classList.add('sub-page');
        document.title = 'Zen Cyclery — Custom Handbuilt Wheels';
        return () => {
            document.documentElement.classList.remove('sub-page');
            document.title = 'Roland Forbes | Software Engineer';
        };
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setHub(null); setRim(null); setSpoke(null);
    }, [bikeType]);

    const opts = CONFIG[bikeType];
    const complete = hub && rim && spoke;

    const OptionRow = ({ item, selected, onSelect }) => (
        <div className={`zc-option${selected ? ' zc-option--active' : ''}`} onClick={() => onSelect(item)}>
            <div className="zc-option-check">{selected ? '●' : '○'}</div>
            <div className="zc-option-info">
                <span className="zc-option-label">{item.label}</span>
                <span className="zc-option-sub">{item.sub}</span>
            </div>
        </div>
    );

    return (
        <div className="zc-page">

            {/* ── Nav ── */}
            <nav className={`zc-nav${scrolled ? ' zc-nav--scrolled' : ''}`}>
                <div className="zc-nav-inner">
                    <div className="zc-logo">ZEN CYCLERY</div>
                    <div className="zc-nav-links">
                        <a href="#build">Build</a>
                        <a href="#picks">Our Picks</a>
                        <a href="#contact">Contact</a>
                    </div>
                    <Link to="/" className="zc-back-link">← Portfolio</Link>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section className="zc-hero">
                <div className="zc-hero-bg" style={{ backgroundImage: `url(${HERO})` }} />
                <div className="zc-hero-content">
                    <p className="zc-hero-sub">Handbuilt wheels. Precision components.</p>
                    <h1 className="zc-hero-h1">ZEN<br />CYCLERY</h1>
                    <a href="#build" className="zc-hero-cta">Build your wheels ↓</a>
                </div>
            </section>

            {/* ── Builder ── */}
            <section className="zc-build" id="build">
                <div className="zc-container">
                    <p className="zc-eyebrow">Custom wheelbuilder</p>
                    <h2 className="zc-section-h2">Choose your components.</h2>

                    <div className="zc-type-toggle">
                        <button className={bikeType === 'road' ? 'active' : ''} onClick={() => setBikeType('road')}>Road</button>
                        <button className={bikeType === 'mtb'  ? 'active' : ''} onClick={() => setBikeType('mtb')}>Mountain</button>
                    </div>

                    <div className="zc-configurator">
                        <div className="zc-config-col">
                            <h4 className="zc-config-group-title">Hub</h4>
                            {opts.hubs.map(h => <OptionRow key={h.id} item={h} selected={hub?.id === h.id} onSelect={setHub} />)}
                        </div>
                        <div className="zc-config-col">
                            <h4 className="zc-config-group-title">Rim</h4>
                            {opts.rims.map(r => <OptionRow key={r.id} item={r} selected={rim?.id === r.id} onSelect={setRim} />)}
                        </div>
                        <div className="zc-config-col">
                            <h4 className="zc-config-group-title">Spokes</h4>
                            {opts.spokes.map(s => <OptionRow key={s.id} item={s} selected={spoke?.id === s.id} onSelect={setSpoke} />)}
                        </div>
                    </div>

                    <a href="#contact" className="zc-btn">
                        {complete ? 'Request This Build' : 'Get in Touch About a Custom Build'}
                    </a>
                </div>
            </section>

            {/* ── Our Picks ── */}
            <section className="zc-picks" id="picks">
                <div className="zc-container">
                    <p className="zc-eyebrow">Curated builds</p>
                    <h2 className="zc-section-h2">Our favorite combinations.</h2>
                    <div className="zc-picks-grid">
                        {PICKS.map(p => (
                            <div className="zc-pick-card" key={p.name}>
                                <div className="zc-pick-img" style={{ backgroundImage: `url(${p.img})` }}>
                                    <span className="zc-pick-type">{p.type}</span>
                                </div>
                                <div className="zc-pick-body">
                                    <h3 className="zc-pick-name">{p.name}</h3>
                                    <p className="zc-pick-desc">{p.desc}</p>
                                    <div className="zc-pick-specs">
                                        <span>{p.hub}</span>
                                        <span>{p.rim}</span>
                                        <span>{p.spoke}</span>
                                    </div>
                                    <div className="zc-pick-weight">{p.weight}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="zc-cta" id="contact">
                <div className="zc-container zc-cta-inner">
                    <h2 className="zc-cta-h2">Ready to ride?</h2>
                    <p className="zc-cta-sub">Every set is built by hand to your spec. Lead time is typically two weeks.</p>
                    <a href="mailto:hello@zencyclery.com" className="zc-btn zc-btn--light">hello@zencyclery.com</a>
                </div>
            </section>

            <footer className="zc-footer">
                <div className="zc-footer-logo">ZEN CYCLERY</div>
                <p className="zc-footer-tagline">Handbuilt wheels. Precision components.</p>
                <div className="zc-footer-bottom">
                    <span>© 2025 Zen Cyclery</span>
                    <Link to="/" className="zc-back-link">Built by Roland Forbes</Link>
                </div>
            </footer>
        </div>
    );
};

export default ZenCyclery;
